import { socketService } from '@/services/socket'
import type { VehicleDataSource } from './types'
import type {
  ApiResp,
  AccessTokenResp,
  LoginRow,
  UserObjType,
  NodeObject,
  BluelinkStreamMsg,
  BluelinkInitialResp,
  FullVehicleStatus2,
} from '@/types/bluelink'

const SESSION_ID_KEY = 'insightssessionid'

function getOrCreateSessionId(): string {
  let sid = localStorage.getItem(SESSION_ID_KEY)
  if (!sid) {
    sid = crypto.randomUUID()
    localStorage.setItem(SESSION_ID_KEY, sid)
  }
  return sid
}

export class NapiDataSource implements VehicleDataSource {
  connect(): void {
    socketService.connect()
  }

  disconnect(): void {
    socketService.disconnect()
  }

  onReconnect(handler: () => void): () => void {
    return socketService.onReconnect(handler)
  }

  onceSessionReady(fn: () => void): void {
    socketService.onceSessionReady(fn)
  }

  get connected(): boolean {
    return socketService.connected
  }

  async loginWithPassword(mobile: string, password: string): Promise<{ accessToken: string; user: UserObjType }> {
    const resp = await socketService.request<ApiResp>('Api', {
      Api: {
        path: '/public/login/login',
        data: {
          mobile,
          password,
          contextId: '',
          domain: window.location.hostname === 'localhost' ? 'ioniq.digify.no' : window.location.hostname,
          SessionID: getOrCreateSessionId(),
          UserAgent: navigator.userAgent,
          keepmesignedin: true,
        },
        settings: { sensitivecontent: true },
      },
    })

    const row = resp.ApiResp?.tables?.[0]?.rows?.[0] as LoginRow | undefined
    if (!row?.AccessToken) {
      throw new Error(row?.StatusDescription || 'Invalid mobile number or password.')
    }

    const authResp = await socketService.request<AccessTokenResp>('AccessToken', {
      AccessToken: {
        AccessToken: row.AccessToken,
        LatestChatMsg: new Date().toISOString(),
        SessionID: getOrCreateSessionId(),
      },
    })

    const user = authResp.AccessTokenResp?.login
    if (!user) {
      throw new Error('Account not provisioned. Contact your administrator.')
    }

    return { accessToken: row.AccessToken, user }
  }

  async reauthWithToken(token: string): Promise<UserObjType | null> {
    socketService.connect()
    const resp = await socketService.request<AccessTokenResp>('AccessToken', {
      AccessToken: {
        AccessToken: token,
        LatestChatMsg: new Date().toISOString(),
        SessionID: getOrCreateSessionId(),
      },
    })
    return resp.AccessTokenResp?.login ?? null
  }

  subscribeStatus(
    onAck: () => void,
    onData: (s: FullVehicleStatus2) => void,
    onError: () => void,
    onMissingVin?: () => void,
  ): () => void {
    const vin = import.meta.env.VITE_VEHICLE_VIN
    if (!vin) {
      if (onMissingVin) onMissingVin()
      else onError()
      return () => {}
    }

    let unsubStream: (() => void) | null = null
    let ackReceived = false

    function setupStream(guid: string) {
      unsubStream?.()
      unsubStream = socketService.subscribe('BluelinkSubscribe', guid, (data: NodeObject) => {
        const msg = data as BluelinkStreamMsg
        if (msg.Bluelink) onData(msg.Bluelink)
      })
    }

    const req = socketService.request<BluelinkInitialResp>(
      'BluelinkSubscribe',
      { BluelinkSubscribe: { VIN: vin } },
      (ack: NodeObject) => {
        ackReceived = true
        setupStream(ack.Guid)
        onAck()
      },
    )

    req.then(
      (resp) => {
        setupStream(resp.Guid)
        if (!ackReceived) onAck()
        const backlog = resp.BluelinkSubscribeResp
        if (Array.isArray(backlog) && backlog.length > 0) {
          onData(backlog[backlog.length - 1])
        }
      },
      () => {
        if (ackReceived) return
        onError()
      },
    )

    return () => unsubStream?.()
  }

  async sendCommand(event: string, payload: Record<string, unknown> = {}): Promise<void> {
    await socketService.request(event, payload)
  }

  forceRefresh(): Promise<void> {
    return Promise.reject(new Error('forceRefresh not supported over socket'))
  }
}
